<template>
    <div
        class="footer bg-black-700 background-container flex flex-col items-center p-12 tablet:p-24 tabletLandscape:py-35 hd:py-40">
        <ul
            class="footer-contact-information flex flex-wrap justify-center list-inside text-gold-500 uppercase text-sm font-bold tracking-wide flex-row mb-6">
            <li>
                <tis-button
                    :url="contact.tollFreePhone | telHref"
                    class="btn-flat-footer-gold"
                    v-text="contact.tollFreePhone" />
            </li>
            <li>
                <tis-button
                    :url="'mailto:' + contact.email"
                    class="btn-flat-footer-gold"
                    v-text="contact.email" />
            </li>
        </ul>
        <ul class="footer-menu-links flex flex-wrap justify-center list-inside text-sm tracking-wider mb-6 text-gray-700">
            <li>
                <component
                    :is="isWebsite ? 'inertia-link' : 'a'"
                    :href="route('web.home')"
                    class="menu-link-gray"
                    v-text="footer.homeLabel" />
            </li>
            <li>
                <component
                    :is="isWebsite ? 'inertia-link' : 'a'"
                    :href="route('web.about')"
                    class="menu-link-gray"
                    v-text="footer.aboutLabel" />
            </li>
            <li>
                <component
                    :is="'a'"
                    href="https://imagesalon.blog"
                    class="menu-link-gray"
                    v-text="footer.blog"
                />
            </li>
            <li>
                <component
                    :is="isWebsite ? 'inertia-link' : 'a'"
                    :href="route('web.contact')"
                    class="menu-link-gray"
                    v-text="footer.contactLabel" />
            </li>
            <li>
                <component
                    :is="isHelpCenter ? 'inertia-link' : 'a'"
                    :href="isHelpCenter ? route('help.index') : route('web.help')"
                    class="menu-link-gray"
                    v-text="footer.helpCenterLabel" />
            </li>
            <li>
                <component
                    :is="isWebsite ? 'inertia-link' : 'a'"
                    :href="route('web.page', {page:'privacy-policy'}).url()"
                    class="menu-link-gray"
                    v-text="footer.privacyLabel" />
            </li>
            <li>
                <component
                    :is="isWebsite ? 'inertia-link' : 'a'"
                    :href="route('web.page', {page:'terms-of-service'}).url()"
                    class="menu-link-gray"
                    v-text="footer.termsLabel" />
            </li>
        </ul>
        <ul
            class="footer-cta flex flex-wrap justify-center list-inside text-gold-500 uppercase text-sm font-bold tracking-wide">
            <li v-if="footer.getStartedLabel">
                <tis-button
                    :url="route('web.registration')"
                    :full-redirect="true"
                    v-text="footer.getStartedLabel"
                />
            </li>
            <li v-if="footer.clientLoginLabel">
                <tis-button
                    :url="route('web.login')"
                    :full-redirect="true"
                    v-text="footer.clientLoginLabel"
                />
            </li>
            <li v-if="footer.clientLogoutLabel">
                <tis-button
                    :url="route('web.logout')"
                    :full-redirect="true"
                    v-text="footer.clientLogoutLabel"
                />
            </li>
        </ul>

        <hr class="border-gray-700 w-5 my-12" />

        <div class="footer-buttons-social flex gap-4 flex-wrap items-stretch">
            <tis-button
                :url="externalLinks.facebookGroup"
                :title="footer.facebookLabel"
                class="btn-inverted-inline p-4 rounded-full m-2 tablet:m-0 tablet:text-sm tracking-wide text-center flex-shrink-0 flex-grow">
                <icon-facebook
                    class="w-4 h-4 tablet:w-6 tablet:h-6 text-gold inline-block" />
            </tis-button>
            <tis-button
                :url="externalLinks.instagram"
                :title="footer.instagramLabel"
                class="btn-inverted-inline p-4 rounded-full m-2 tablet:m-0 tablet:text-sm tracking-wide flex-shrink-0 flex-grow">
                <icon-instagram
                    class="w-4 h-4 tablet:w-6 tablet:h-6 text-gold-500 inline-block" />
            </tis-button>
            <tis-button
                :url="externalLinks.linkedin"
                :title="footer.linkedinLabel"
                class="btn-inverted-inline p-4 rounded-full m-2 tablet:m-0 tablet:text-sm tracking-wide flex-shrink-0 flex-grow">
                <icon-linkedin
                    class="w-4 h-4 tablet:w-6 tablet:h-6 text-gold-500 inline-block" />
            </tis-button>
            <tis-button
                :url="externalLinks.youtube"
                :title="footer.youtubeLabel"
                class="btn-inverted-inline p-4 rounded-full m-2 tablet:m-0 tablet:text-sm tracking-wide flex-shrink-0 flex-grow">
                <icon-youtube
                    class="w-4 h-4 tablet:w-6 tablet:h-6 text-gold-500 inline-block" />
            </tis-button>
        </div>

        <hr class="border-gray-700 w-5 my-12" />

        <div class="footer-copyright text-center text-xs">
            <p class="uppercase text-gray-700 tracking-wide">
                {{ footer.copyright.title }}
            </p>
            <p class="uppercase text-gray-700 tracking-wide">
                {{ footer.copyright.subtitleOne }}
                <svg-icon
                    icon="heart"
                    class="w-4 h-4 inline-block mx-1" />
                {{ footer.copyright.subtitleTwo }}
            </p>
        </div>
    </div>
</template>

<script>
import TisButton from '@whisper/components/buttons/TisButton';
import SvgIcon from '@whisper/components/icons/SvgIcon';
import IconFacebook from '@whisper/components/icons/footer/Facebook';
import IconInstagram from '@whisper/components/icons/footer/Instagram';
import IconLinkedin from '@whisper/components/icons/footer/Linkedin';
import IconYoutube from '@whisper/components/icons/footer/Youtube';
import { telHref } from '@whisper/filters/telHref';

export default {
  components: {
    TisButton,
    SvgIcon,
    IconFacebook,
    IconInstagram,
    IconLinkedin,
    IconYoutube,
  },
  filters: {
    telHref,
  },
  props: ['contact', 'footer', 'externalLinks', 'context'],
  computed: {
    isHelpCenter() {
      return this.context === 'help';
    },
    isWebsite() {
      return this.context === 'website';
    },
  },
};
</script>
<style lang="scss" scoped>
    .footer-menu-links,
    .footer-contact-information,
    .footer-cta {
        li {
            @apply mx-0 px-0;

            &:before {
                content: '• ';
                @apply mx-2;
            }

            &:first-child {
                &:before {
                    @apply hidden;
                }
            }

            a {
                position: relative;
                left: -3px;
                text-transform: uppercase;
                @apply duration-200 border-b border-transparent;
                // note: we don't use transition-all anymore
                // because since tailwind 1.2
                // it cannot be used with @apply
                // due to having responsive variants
                // in any case, having a more specific transition-property
                // is actually cleaner and more performant
                transition-property: border;
                &:hover {
                    @apply border-b border-gold-500;
                }

                &.menu-link-gray {
                    &:hover {
                        @apply border-b border-gray-700;
                    }
                }
            }
        }
    }
</style>
